// Components
import { BaseTable, Button, Card, Checkbox, DateInput, FooterMenu, HeaderMenu, Input, Loading, Select, TableHeader, TableSection, Textarea } from "components"
import { DetailSection } from "../ReturnItems/components"

// Icons
import { IoSendSharp } from "react-icons/io5"

// Third-Party Libraries
import moment from "moment"
import { Link, useParams } from "react-router-dom"

// Utils
import { convertNumber, useEmployee } from "utils"
import { useDetailSalesOrder, useDropdownCustomer } from "../CustomerOrder/utils/hooks"
import { statusList, useDetail } from "./utils"
import { useDetailInvoice } from "../Invoice/utils/hooks"
import { Journal } from "./components"

export default function EditReturnRecord(): JSX.Element {
  // Hooks
  const { id } = useParams()
  const { data, isLoading } = useDetail(id)
  const invoice = useDetailInvoice({
    invoice_id: data?.datareturn.invoice_id.toString() ?? ""
  })
  
  // Vars
  const sum: {
    subtotal: number
    tax: number
  } = (data?.detailreturn ?? []).reduce((acc, item) => {
    // Vars
    const tax = item.total_return * item.tax_rate / 100

    return {
      subtotal: acc.subtotal + item.total_return,
      tax: acc.tax + tax
    }
  }, {
    subtotal: 0,
    tax: 0
  })
  const subtotal = sum.subtotal
  const tax = sum.tax
  const total = subtotal + tax

  // useEffect(() => {
  //   if (data?.datareturn) {
  //     invoice.fetchData(data.datareturn.invoice_id)
  //   }

  //   // eslint-disable-next-line
  // }, [data?.datareturn])

  return (
    <div className="container my-5 flex flex-col gap-3">
      {isLoading || !data ? (
        <Loading
          errorText="No data available"
          loading={isLoading}
        />
      ) : (
        <>
          <HeaderMenu title="DATA ENTRY | RETURN ITEM">
            {data.datareturn.trx_code}
          </HeaderMenu>

          <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
            <Select
              isDisabled
              label="CUSTOMER"
              placeholder="Select Customer"
              options={[
                {
                  label: data.datareturn.customer_name,
                  value: data.datareturn.customer_id.toString()
                }
              ]}
              value={{
                label: data.datareturn.customer_name,
                value: data.datareturn.customer_id.toString()
              }}
            />

            <CustomerDetail customer_id={data.datareturn.customer_id} />

            <section className="lg:justify-self-end mb-3">
              <Checkbox readOnly value>
                TAX-INCLUSIVE
              </Checkbox>
            </section>
          </section>

          <Card>
            <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
              <section className="flex flex-col gap-3">
                <section>
                  <Textarea
                    disabled
                    label="SHIPPED"
                    value={data.datareturn.address}
                  />
                </section>

                <Input
                  disabled
                  label="MEMO"
                  value={data.datareturn.memo}
                />

                <Select
                  isDisabled
                  label="LOCATION"
                  placeholder="Select Location"
                  options={[
                    {
                      label: data.datareturn.location_name,
                      value: data.datareturn.location_id.toString()
                    }
                  ]}
                  value={{
                    label: data.datareturn.location_name,
                    value: data.datareturn.location_id.toString()
                  }}
                />

                <section className="flex items-end">
                  <Input
                    disabled
                    className="grow"
                    label="LINK"
                    value={invoice.data?.datainvoice.trx_code}
                  />

                  {invoice.data && (
                    <DetailSection
                      sales_order_id={invoice.data?.datainvoice.sales_order_id.toString()}
                      invoice_id={data.datareturn.invoice_id.toString()}
                    />
                  )}
                </section>
              </section>

              <section className="flex flex-col gap-3">
                <DateInput
                  disabled
                  label="DATE"
                  selected={moment(data.datareturn.transaction_date).toDate()}
                  onChange={() => {}}
                />

                {/* <Input
                  disabled
                  label="DELIVERED"
                  value={invoice.data?.delivery_no}
                /> */}

                <Input
                  disabled
                  label="REFERENCE"
                  value={data.datareturn.referensi}
                />

                {invoice.data && <EmployeeSection sales_order_id={invoice.data?.datainvoice.sales_order_id} />}
              </section>
            </Card.Body>
          </Card>

          <BaseTable>
            <TableHeader>
              <tr>
                <th>QTY RETURNED</th>
                <th>QTY REFUNDED</th>
                <th>BACK RETURNED</th>
                <th>UNIT</th>
                <th>ITEM CODE</th>
                <th>DESCRIPTION</th>
                <th>PRICE</th>
                <th>DISC (%)</th>
                <th>TOTAL</th>
                <th>TAX</th>
              </tr>
            </TableHeader>

            <tbody>
              {!data.detailreturn.length ? (
                <tr>
                  <td colSpan={10}>
                    <Loading errorText="No data available" />
                  </td>
                </tr>
              ) : data.detailreturn.map((item, key) => {
                return (
                  <tr key={key}>
                    <td className="text-right">{convertNumber(item.quantity_return).intoNormalAmount}</td>
                    <td className="text-right">{convertNumber(item.qty_refunded).intoNormalAmount}</td>
                    <td className="text-right">{convertNumber(item.back_returned).intoNormalAmount}</td>
                    <td>{item.unit_sell_measure_name}</td>
                    <td>{item.product_barcode}</td>
                    <td>{item.product_name}</td>
                    <td className="text-right">{convertNumber(item.price_return).intoCurrency}</td>
                    <td className="text-right">{convertNumber(item.discount_return).intoCurrency}%</td>
                    <td className="text-right">{convertNumber(item.total_return).intoCurrency}</td>
                    <td>{item.tax_name} ({convertNumber(item.tax_rate).intoCurrency}%)</td>
                  </tr>
                )
              })}
            </tbody>
          </BaseTable>

          <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <TableSection>
                <TableSection.Row
                  label="RETURN STATUS"
                  value={statusList.find(item => item.value === data.datareturn.return_status.toString())?.label ?? "-"}
                />

                <TableSection.Row
                  label="APPROVE STATUS"
                  value={data.datareturn.approve_status_text}
                />

                <TableSection.Row
                  label="APPROVED BY"
                  value={data.datareturn.approve_by_name ?? "-"}
                />
              </TableSection>
            </section>

            <section className="flex flex-col lg:items-end gap-3">
              <BaseTable
                plain
                className="w-full min-w-[100px] md:max-w-[400px] text-right">
                <tbody>
                  <tr>
                    <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                    <td>{convertNumber(subtotal).intoCurrency}</td>
                  </tr>

                  <tr>
                    <td className="w-[1px] whitespace-nowrap">TAX</td>
                    <td>{convertNumber(tax).intoCurrency}</td>
                  </tr>

                  <tr className="font-bold">
                    <td className="w-[1px] whitespace-nowrap">TOTAL RETURN</td>
                    <td>{convertNumber(total).intoCurrency}</td>
                  </tr>

                  <tr className="font-bold">
                    <td className="w-[1px] whitespace-nowrap">BALANCE</td>
                    <td>{convertNumber(total).intoCurrency}</td>
                  </tr>
                </tbody>
              </BaseTable>
            </section>
          </section>

          <section className="flex flex-wrap gap-3">
            <Link to={`/sales/transaction/refund-return-item/${id}`}>
              <Button color="primary">REFUND ITEM</Button>
            </Link>

            <Link to={`/sales/transaction/refund-return-money/${id}`}>
              <Button color="primary">REFUND MONEY</Button>
            </Link>

            <Link to={`/sales/transaction/refund-return-adjustment/${id}`}>
              <Button color="primary">ADJUSTMENT</Button>
            </Link>
          </section>

          <FooterMenu
            hasCancelButton
            attachment={{ actiontype: "attachment" }}
            customElement={{
              journal:<Journal data={data} invoice={invoice.data?.detailinvoice ??[]}/>
            }}
          />
        </>
      )}
    </div>
  )
}

function CustomerDetail(params: {
  customer_id: number
}): JSX.Element {
  // Hooks
  const { data } = useDropdownCustomer()

  // Vars
  const customer = data.find(item => item.customer_id === params.customer_id)

  return (
    <section>
      <div className="normal-case flex justify-evenly items-center pt-10">
        <div className="flex flex-col gap-2">
          <span>TRADING TERM </span>
          <span>PRICE LEVEL</span>
        </div>

        <div className="px-2 flex flex-col gap-2">
          <IoSendSharp />
          <IoSendSharp />
        </div>

        <div className="flex flex-col gap-2">
          <span>Net {customer?.payment_term_name ?? 0} Days after OEM </span>
          <span>{customer?.price_level_name ?? "-"}</span>
        </div>
      </div>
    </section>
  )
}

function EmployeeSection(params: {
  sales_order_id: number
}): JSX.Element {
  // Hooks
  const employee = useEmployee()
  const so = useDetailSalesOrder({ sales_order_id: params.sales_order_id.toString() })

  return (
    <Select
      isDisabled
      label="EMPLOYEE"
      placeholder="Select Employee"
      isLoading={employee.isLoadingEmployee || so.isLoading}
      options={employee.dataEmployee}
      value={employee.dataEmployee.find(item => item.value === so.data?.dataso.employee_id.toString()) ?? null}
    />
  )
}